window.sleepwhaleEngine = {
  scoreQuiz: function({ answers, steps, config, demographics = {} }) {
    const buckets = config.buckets || [];
    const tallies = config.tallies || [];

    const barScores   = this.zeroed(buckets);
    const tallyScores = this.zeroed(tallies);
    const flags       = new Set();
    const bucketCaps  = [];

    // 1. Walk the answered steps
    for (const step of steps) {
      const selected = this.selectedOptions(step, answers);
      if (!selected.length) continue;

      const stepSubtotal = this.zeroed(buckets);
      for (const option of selected) {
        for (const [name, points] of Object.entries(option.weights || {})) {
          if (buckets.includes(name)) stepSubtotal[name] += points;
          else if (tallies.includes(name)) tallyScores[name] += points;
        }
        for (const flag of option.flags || []) flags.add(flag);
        if (option.capBuckets) bucketCaps.push(option.capBuckets);
      }

      const limit = step.maxContribution || {};
      for (const bucket of buckets) {
        const capped = limit[bucket] !== undefined
          ? Math.min(stepSubtotal[bucket], limit[bucket])
          : stepSubtotal[bucket];
        barScores[bucket] += capped;
      }
    }

    // 2. Caps
    for (const cap of bucketCaps) {
      for (const [bucket, max] of Object.entries(cap)) {
        barScores[bucket] = Math.min(barScores[bucket], max);
      }
    }

    // 3. Flags from tallies
    for (const [flag, rule] of Object.entries(config.flagsFromTallies || {})) {
      if (tallyScores[rule.tally] >= rule.atLeast) flags.add(flag);
    }

    // 4. Ceilings & Normalisation
    const ceilings = this.deriveCeilings(steps, buckets);
    const share = {};
    for (const bucket of buckets) {
      share[bucket] = ceilings[bucket] > 0 ? barScores[bucket] / ceilings[bucket] : 0;
    }

    const highestShare = Math.max(...Object.values(share));
    if (highestShare === 0) return null; 

    // 5. Bar heights
    const scale = config.display.scaleMax || 4;
    const bars = {};
    for (const bucket of buckets) {
      bars[bucket] = Math.round(scale * share[bucket]);
    }

    // 6. Core Issue Bump
    const display = config.display;
    const coreIssueBucket = this.rankedByShare(buckets, share, config.tieBreak)[0];
    bars[coreIssueBucket] = Math.min(
      scale,
      Math.max(display.minCoreLevel || 1, bars[coreIssueBucket] + (display.coreIssueBump || 1))
    );

    // 7. Routing
    const context = {
      tallyScores, flags,
      isWoman: demographics.gender === config.routing.womenValue,
      thresholds: config.thresholds,
    };

    const rankedBuckets = this.rankedByShare(buckets, share, config.tieBreak);
    let profileId = config.routing.lowConfidenceProfile;

    for (const bucket of rankedBuckets) {
      if (barScores[bucket] < config.minEvidence) continue;

      const direct = config.routing.byPrimaryBucket[bucket];
      if (direct === undefined) { profileId = this.runChain(config, context); break; }

      const guard = config.routing.guards[String(direct)];
      if (guard && !flags.has(guard.requiresEvidence)) {
        profileId = guard.fallbackToChain ? this.runChain(config, context) : null;
        if (profileId !== null) break;
        continue;
      }
      profileId = direct;
      break;
    }
    if (profileId === null) profileId = config.routing.lowConfidenceProfile;

    // --- Calculate Demographic Segment & Commonality ---
    const segment = this.getDemographicSegment(demographics, config);
    const content = typeof window !== 'undefined' ? window.QUIZ_CONTENT : null;
    const commonality = this.resolveCommonality(profileId, segment, content);
    const personalizedContent = this.resolveProfileContent(profileId, demographics, flags, content);
    
    // 8. Output exact data shape needed for Alpine
    return {
      profile: profileId,
      buckets: bars, 
      tallies: tallyScores,
      flags: Array.from(flags),
      segment: segment,
      commonality: commonality,
      resolvedText: personalizedContent 
    };
  },

  // --- ENGINE HELPERS ---
  zeroed: function(names) {
    return Object.fromEntries(names.map((n) => [n, 0]));
  },
  rankedByShare: function(buckets, share, tieBreak) {
    return [...buckets].sort((a, b) => share[b] - share[a] || (tieBreak.indexOf(a) - tieBreak.indexOf(b)));
  },
  selectedOptions: function(step, answers) {
    const given = answers[step.value ?? step.name ?? step.id]; 
    if (given === undefined || given === null) return [];
    const chosen = Array.isArray(given) ? given : [given];
    return (step.options || []).filter((o) => chosen.includes(o.value));
  },
  deriveCeilings: function(steps, buckets) {
    const ceilings = this.zeroed(buckets);
    for (const step of steps) {
      const perBucket = this.zeroed(buckets);
      const best = this.zeroed(buckets);
      for (const option of step.options || []) {
        if (option.capBuckets && !option.weights) continue;
        for (const [name, points] of Object.entries(option.weights || {})) {
          if (!buckets.includes(name)) continue;
          perBucket[name] += points;
          best[name] = Math.max(best[name], points);
        }
      }
      const limit = step.maxContribution || {};
      for (const bucket of buckets) {
        const raw = step.multiSelect ? perBucket[bucket] : best[bucket];
        ceilings[bucket] += limit[bucket] !== undefined ? Math.min(raw, limit[bucket]) : raw;
      }
    }
    return ceilings;
  },
  runChain: function(config, { tallyScores, flags, isWoman, thresholds }) {
    for (const rule of config.routing.chain) {
      if (rule.tally && tallyScores[rule.tally] >= (rule.atLeast ?? thresholds[rule.tally])) {
        if (!rule.womenOnly || isWoman) {
          if (!rule.requiresConflict || this.hasConflict(config, tallyScores, flags, thresholds)) {
            return rule.profile;
          }
        }
      }
      if (rule.flag && flags.has(rule.flag)) return rule.profile;
      if (rule.otherwise) return rule.profile;
    }
    return config.routing.lowConfidenceProfile;
  },
  hasConflict: function(config, tallyScores, flags, thresholds) {
    return (config.routing.conflictWhen || []).every((c) =>
      c.flag ? flags.has(c.flag) : tallyScores[c.tally] >= (c.atLeast ?? thresholds[c.tally])
    );
  },

  // --- Demographic & Commonality Helpers ---
  getDemographicSegment: function(demographics, config) {
    const womenVal = config?.routing?.womenValue || 'women';
    const isWoman = demographics.gender === womenVal;
    
    // Safely assume non-woman with a defined gender is a man for this specific matrix
    const isMan = demographics.gender && !isWoman; 
    
    // Accommodates multiple age tracking formats (e.g., '60+', 65, etc)
    const is60Plus = demographics.age === '60+' || parseInt(demographics.age) >= 60 || demographics.ageRange === '60+';

    if (isWoman && is60Plus) return 'Women 60+';
    if (isMan && is60Plus) return 'Men 60+';
    if (is60Plus) return 'All 60+';
    if (isWoman) return 'All women';
    if (isMan) return 'All men';
    
    return 'Default';
  },
  resolveCommonality: function(profileId, segment, content) {
    // Safe default in case the matrix has gaps
    const fallback = { numerator: 1, denominator: 5, text: "1 in 5" };
    if (!content?.profiles?.[profileId]?.commonality) return fallback;

    const comms = content.profiles[profileId].commonality;
    
    // Find exact segment match
    let ratio = comms[segment];
    
    // If the table explicitly has a '—' (null), fallback to broader demographic pools
    if (!ratio) {
      if (segment.includes('Women')) ratio = comms['All women'];
      else if (segment.includes('Men')) ratio = comms['All men'];
      else if (segment.includes('60+')) ratio = comms['All 60+'];
    }
    
    // Final safety net
    if (!ratio) ratio = comms['Default'] || [1, 5];

    return {
      numerator: ratio[0],
      denominator: ratio[1],
      text: `${ratio[0]} in ${ratio[1]}`
    };
  },
 resolveProfileContent: function(profileId, demographics, flags, content) {
    const profile = content?.profiles?.[profileId];
    if (!profile) return { description: "", recommendationHtml: "" };

    // Start with the defaults
    let finalDesc = profile.description || "";
    let finalRec = profile.recommendationHtml || "";

    // If variants exist, check them top to bottom
    if (profile.variants && Array.isArray(profile.variants)) {
      for (const variant of profile.variants) {
        const cond = variant.conditions || {};
        
        let match = true;
        
        // Check gender requirement
        if (cond.gender && cond.gender !== demographics.gender) {
          match = false;
        }
        
        // Check age requirement (matches exact string from your age-v2 answer, e.g., '60_69')
        if (cond.age && cond.age !== demographics.age) {
          match = false;
        }
        
        // Check flag requirement
        if (cond.flag && !flags.has(cond.flag)) {
          match = false;
        }

        // If it matches all defined conditions, apply text and stop looking
        if (match) {
          if (variant.description) finalDesc = variant.description;
          if (variant.recommendationHtml) finalRec = variant.recommendationHtml;
          break; 
        }
      }
    }

    return {
      description: finalDesc,
      recommendationHtml: finalRec
    };
  }
};